import { uiText, useUiLanguage, currentLocale, statusText } from '@/i18n/interface';

import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Eye, Users, Calendar, CreditCard, Settings, AlertTriangle, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Dojo {
  id: string;
  name: string;
  martial_art_type: string;
  martial_art_style: string;
  plan: string;
  is_active: boolean;
  student_limit: number;
  created_at: string;
  trial_start?: string;
}

interface DojoUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  join_date: string;
  dojo_id: string;
}

const DojoManagement: React.FC = () => {
  useUiLanguage();
  const [dojos, setDojos] = useState<Dojo[]>([]);
  const [selectedDojo, setSelectedDojo] = useState<string>('');
  const [dojoDetails, setDojoDetails] = useState<Dojo | null>(null);
  const [dojoUsers, setDojoUsers] = useState<DojoUser[]>([]);
  const [newStudentLimit, setNewStudentLimit] = useState<number>(100);
  const [loading, setLoading] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { startImpersonation, profile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchDojos();
  }, []);

  const fetchDojos = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching all dojos...');

      const { data, error } = await supabase
        .from('dojos')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching dojos:', error);
        throw error;
      }

      console.log('📊 Dojos found:', data?.length);
      setDojos(data || []);
    } catch (error) {
      console.error('Error fetching dojos:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to fetch dojos"),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDojoDetails = async (dojoId: string) => {
    if (!dojoId) return;

    try {
      setLoading(true);
      console.log('🔍 Fetching details for dojo:', dojoId);

      // Fetch dojo details
      const { data: dojoData, error: dojoError } = await supabase
        .from('dojos')
        .select('*')
        .eq('id', dojoId)
        .single();

      if (dojoError) {
        console.error('❌ Error fetching dojo details:', dojoError);
        throw dojoError;
      }

      console.log('📊 Dojo details:', dojoData);

      // Fetch dojo users
      console.log('🔍 Fetching users for dojo:', dojoId);
      const { data: usersData, error: usersError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, role, join_date, dojo_id')
        .eq('dojo_id', dojoId)
        .order('role', { ascending: true });

      console.log('📊 Users query result:', { usersData, usersError });
      console.log('👥 Users found:', usersData?.length, 'users');
      usersData?.forEach(user => {
        console.log(`- ${user.first_name} ${user.last_name} (${user.role}) - dojo: ${user.dojo_id}`);
      });

      if (usersError) {
        console.error('❌ Error fetching users:', usersError);
        throw usersError;
      }

      setDojoDetails(dojoData);
      setDojoUsers(usersData || []);
      setNewStudentLimit(dojoData.student_limit || 100);
    } catch (error) {
      console.error('Error fetching dojo details:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to fetch dojo details"),
        variant: 'destructive'
      });
      // Reset state on error
      setDojoDetails(null);
      setDojoUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDojoSelect = (dojoId: string) => {
    console.log('🎯 Selecting dojo:', dojoId);
    setSelectedDojo(dojoId);

    // Reset previous state
    setDojoDetails(null);
    setDojoUsers([]);

    // Fetch new details
    if (dojoId) {
      fetchDojoDetails(dojoId);
    }
  };

  const handleImpersonate = async () => {
    if (!dojoDetails) {
      toast({
        title: uiText("Error"),
        description: uiText("No dojo selected for impersonation"),
        variant: 'destructive'
      });
      return;
    }

    // Check if user has super_user permissions
    if (profile?.role !== 'super_user') {
      toast({
        title: uiText("Access Denied"),
        description: uiText("Only super users can impersonate dojos"),
        variant: 'destructive'
      });
      return;
    }

    try {
      setImpersonating(true);
      console.log('🎭 Starting impersonation for dojo:', dojoDetails.name);

      // Start impersonation
      startImpersonation(dojoDetails.id, dojoDetails.name);

      toast({
        title: uiText("Impersonation Active"),
        description: uiText("Now viewing as admin of {{value0}}", { value0: dojoDetails.name }),
      });

      // Small delay to ensure state is properly updated
      setTimeout(() => {
        console.log('🔄 Redirecting to admin dashboard...');
        navigate('/admindashboard');
      }, 500);

    } catch (error) {
      console.error('❌ Error during impersonation:', error);
      toast({
        title: uiText("Impersonation Failed"),
        description: uiText("Failed to start impersonation. Please try again."),
        variant: 'destructive'
      });
      setImpersonating(false);
    }
  };

  const updateStudentLimit = async () => {
    if (!dojoDetails) return;

    try {
      setLoading(true);
      console.log('📝 Updating student limit for dojo:', dojoDetails.id, 'to:', newStudentLimit);

      const { error } = await supabase.rpc('update_dojo_student_limit', {
        dojo_id: dojoDetails.id,
        new_limit: newStudentLimit
      });

      if (error) {
        console.error('❌ Error updating student limit:', error);
        throw error;
      }

      toast({
        title: uiText("Success"),
        description: uiText("Student limit updated successfully")
      });

      // Refresh dojo details
      await fetchDojoDetails(dojoDetails.id);
    } catch (error) {
      console.error('Error updating student limit:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to update student limit"),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDojo = async () => {
    if (!dojoDetails) return;

    // Extra protection - don't allow deletion of Taiga Dojo
    if (dojoDetails.name === 'Taiga Dojo') {
      toast({
        title: uiText("Cannot Delete"),
        description: uiText("Taiga Dojo cannot be deleted"),
        variant: 'destructive'
      });
      return;
    }

    try {
      setDeleting(true);
      console.log('🗑️ Deleting dojo completely:', dojoDetails.name);
      console.log('🔍 Dojo details:', {
        id: dojoDetails.id,
        name: dojoDetails.name,
        userCount: dojoUsers.length
      });

      // Check current user permissions and profile
      const { data: currentUser } = await supabase.auth.getUser();
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', currentUser?.user?.id)
        .single();

      console.log('👤 Current user for deletion:', {
        userId: currentUser?.user?.id,
        email: currentUser?.user?.email,
        profile: currentProfile
      });

      const { data, error } = await supabase.rpc('delete_dojo_completely', {
        target_dojo_id: dojoDetails.id
      });

      if (error) {
        console.error('❌ Error deleting dojo:', error);
        console.error('❌ Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });

        // Provide more specific error messages
        let errorMessage = uiText("Failed to delete dojo. Please try again.");
        if (error.code === '23503') {
          errorMessage = uiText("Cannot delete dojo due to existing references. Please contact support.");
        } else if (error.code === 'P0001') {
          errorMessage = error.message || uiText("Delete operation not permitted.");
        } else if (error.message) {
          errorMessage = error.message;
        }

        toast({
          title: uiText("Delete Failed"),
          description: errorMessage,
          variant: 'destructive'
        });
        return;
      }

      console.log('✅ Dojo deletion result:', data);

      toast({
        title: uiText("Dojo Deleted"),
        description: uiText("{{value0}} has been completely deleted", { value0: dojoDetails.name }),
      });

      // Reset state and refresh dojos list
      setSelectedDojo('');
      setDojoDetails(null);
      setDojoUsers([]);
      setShowDeleteConfirm(false);
      await fetchDojos();

    } catch (error) {
      console.error('Unexpected error during dojo deletion:', error);
      if (!error.code) {
        toast({
          title: uiText("Delete Failed"),
          description: uiText("An unexpected error occurred. Please try again."),
          variant: 'destructive'
        });
      }
    } finally {
      setDeleting(false);
    }
  };

  const getPlanBadgeColor = (plan: string) => {
    switch (plan.toLowerCase()) {
      case 'pro': return 'bg-green-500';
      case 'premium': return 'bg-blue-500';
      case 'trial': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusBadgeColor = (isActive: boolean) => {
    return isActive ? 'bg-green-500' : 'bg-red-500';
  };

  if (loading && dojos.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>{uiText("Loading dojos...")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Dojo Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            {uiText("Dojo Management")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label htmlFor="dojo-select">{uiText("Select a dojo to manage")}</Label>
              <Select value={selectedDojo} onValueChange={handleDojoSelect}>
                <SelectTrigger id="dojo-select">
                  <SelectValue placeholder={uiText("Select a dojo to view details")} />
                </SelectTrigger>
                <SelectContent>
                  {dojos.map((dojo) => (
                    <SelectItem key={dojo.id} value={dojo.id}>
                      <div className="flex items-center gap-2">
                        <span>{dojo.name}</span>
                        <Badge variant="outline" className={`${getPlanBadgeColor(dojo.plan)} text-white`}>
                          {dojo.plan}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {dojoDetails && (
              <div className="flex gap-2">
                <Button
                  onClick={handleImpersonate}
                  disabled={impersonating || loading}
                  className="flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  {impersonating ? uiText("Starting...") : uiText("Impersonate Dojo")}
                </Button>
                <Button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleting || loading || dojoDetails.name === 'Taiga Dojo'}
                  variant="destructive"
                  className="flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  {uiText("Delete Dojo")}</Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dojo Details */}
      {dojoDetails && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Dojo Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {uiText("Dojo Information")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{uiText("Dojo Name")}</Label>
                <p className="font-medium">{dojoDetails.name}</p>
              </div>
              <div>
                <Label>{uiText("Martial Art")}</Label>
                <p>{dojoDetails.martial_art_type} - {dojoDetails.martial_art_style}</p>
              </div>
              <div className="flex items-center gap-4">
                <div>
                  <Label>{uiText("Plan")}</Label>
                  <div className="mt-1">
                    <Badge className={`${getPlanBadgeColor(dojoDetails.plan)} text-white`}>
                      {dojoDetails.plan.toUpperCase()}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label>{uiText("Status")}</Label>
                  <div className="mt-1">
                    <Badge className={`${getStatusBadgeColor(dojoDetails.is_active)} text-white`}>
                      {dojoDetails.is_active ? uiText("ACTIVE") : uiText("INACTIVE")}
                    </Badge>
                  </div>
                </div>
              </div>
              <div>
                <Label>{uiText("Created")}</Label>
                <p>{new Date(dojoDetails.created_at).toLocaleDateString(currentLocale())}</p>
              </div>
              {dojoDetails.trial_start && (
                <div>
                  <Label>{uiText("Trial Started")}</Label>
                  <p>{new Date(dojoDetails.trial_start).toLocaleDateString(currentLocale())}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Student Limit Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {uiText("Student Management")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{uiText("Current Users:")}{' '}{dojoUsers.length}</Label>
                <div className="text-sm text-muted-foreground">
                  {uiText("Admins:")}{' '}{dojoUsers.filter(u => u.role === 'admin').length} {' '}{uiText("| Students:")}{' '}{dojoUsers.filter(u => u.role === 'student').length}
                </div>
              </div>
              <Separator />
              <div>
                <Label htmlFor="student-limit">{uiText("Student Limit")}</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    id="student-limit"
                    type="number"
                    value={newStudentLimit}
                    onChange={(e) => setNewStudentLimit(parseInt(e.target.value) || 0)}
                    className="flex-1"
                    min="0"
                    max="1000"
                  />
                  <Button
                    onClick={updateStudentLimit}
                    disabled={loading || newStudentLimit === dojoDetails.student_limit}
                    size="sm"
                  >
                    {uiText("Update")}</Button>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {uiText("Current limit:")}{' '}{dojoDetails.student_limit || 100}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Users List */}
      {dojoUsers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {uiText("Dojo Users (")}{dojoUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {dojoUsers.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">{user.first_name} {user.last_name}</p>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                  <div className="text-right">
                    <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                      {statusText(user.role)}
                    </Badge>
                    {user.join_date && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {uiText("Joined:")}{' '}{new Date(user.join_date).toLocaleDateString(currentLocale())}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Dojo Selected State */}
      {!selectedDojo && (
        <Card>
          <CardContent className="text-center py-12">
            <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">{uiText("No Dojo Selected")}</h3>
            <p className="text-muted-foreground">
              {uiText("Please select a dojo from the dropdown above to view its details and manage settings.")}</p>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">{uiText("Delete Dojo Permanently")}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <div>
                  {uiText("Are you sure you want to")}{' '}<strong>{uiText("permanently delete")}</strong> "{dojoDetails?.name}"?
                </div>
                <div className="text-sm text-muted-foreground">
                  {uiText("This action will completely remove:")}</div>
                <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                  <li>{uiText("All user profiles and accounts (")}{dojoUsers.length} {' '}{uiText("users)")}</li>
                  <li>{uiText("All training sessions and attendance records")}</li>
                  <li>{uiText("All payments, invoices, and financial data")}</li>
                  <li>{uiText("All dojo settings and configurations")}</li>
                  <li>{uiText("All associated data and files")}</li>
                </ul>
                <div className="font-medium text-red-600 mt-4">
                  {uiText("This action cannot be undone!")}</div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{uiText("Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDojo}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? uiText("Deleting...") : uiText("Delete Permanently")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DojoManagement;
